import { Dayjs } from 'dayjs'
import { createSelector } from 'reselect'

import { getOnlyDate } from '~/astra'
import { StoreState, useStore } from '~/store/store'
import { ClosedLocationText, getOverrideClosedLocationText, getServicePeriodClosedLocationText } from '~/utils/closedLocationText'
import { keyOverrideByLocationDay } from '~/utils/locationServiceOverride'
import { getOpeningHours } from '~/utils/openingHours'
import { findServicePeriod, getClosedWeekdaysInSameRange, ServicePeriod } from '~/utils/servicePeriods'

import { Location, LocationServiceOverride, selectLocationServiceOverrides } from '../'

// The location is closed for the day through a LocationServiceOverride ("closing room" in a calendar).
export type ClosedLocationOverrideInfo = {
    type: 'location_service_override'
    closedReason: LocationServiceOverride['closed_reason']
    locationServiceOverride: LocationServiceOverride
} & ClosedLocationText

// The location is closed through a service period in "Sykehusoppsett".
export type ClosedLocationServicePeriodInfo = {
    type: 'service_period'
    closedReason: 'service_period'
    servicePeriod: ServicePeriod
    // Working weekdays closed across all periods sharing this period's date range.
    closedWeekdays: string[]
} & ClosedLocationText

export type ClosedLocationInfo = ClosedLocationOverrideInfo | ClosedLocationServicePeriodInfo

export const selectGetClosedLocationInfo = createSelector(
    (state: StoreState) => selectLocationServiceOverrides(state, { keyBy: keyOverrideByLocationDay }),
    overridesByLocationDay => {
        const byLocationDay = (location: Location, date: Dayjs): ClosedLocationInfo | undefined => {
            const locationServiceOverride = overridesByLocationDay.get(
                keyOverrideByLocationDay({ location_uuid: location.uuid, scheduled_date: getOnlyDate(date) })
            )
            // Reuse getOpeningHours so the closed_location_days feature flag and
            // override-over-service-period precedence stay a single source of truth.
            const openingStatus = getOpeningHours({ date, location, locationServiceOverride })
            if (!openingStatus.isClosed) {
                return undefined
            }
            const { closedReason } = openingStatus

            if (locationServiceOverride) {
                return {
                    type: 'location_service_override',
                    closedReason: locationServiceOverride.closed_reason,
                    locationServiceOverride,
                    ...getOverrideClosedLocationText(locationServiceOverride),
                }
            }

            if (closedReason === 'service_period') {
                const servicePeriod = findServicePeriod({ date, location })
                if (!servicePeriod) {
                    return undefined
                }
                const closedWeekdays = getClosedWeekdaysInSameRange(servicePeriod, location.service_periods ?? [])
                return {
                    type: 'service_period',
                    closedReason: 'service_period',
                    servicePeriod,
                    closedWeekdays,
                    ...getServicePeriodClosedLocationText(servicePeriod, closedWeekdays),
                }
            }

            return undefined
        }

        return { byLocationDay }
    }
)

export const useClosedLocationInfo = ({ date, location }: { date: Dayjs; location: Location }) =>
    useStore(selectGetClosedLocationInfo).byLocationDay(location, date)
